import classNames from 'classnames';
import { arc, type PieArcDatum, pie } from 'd3';
import { type CSSProperties, useRef, useState } from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  getDimmedPlotColor,
  getFormatter,
  getPrimaryColor,
  useChartTheme,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { Legend } from '../legend/legend';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { CursorTooltip } from '../plot-tooltip';
import styles from './pie-chart.module.css';

const MIN_SIZE = 40;
const LEGEND_HEIGHT = 40;

export type PieData<TData extends object = object> = {
  label: string;
  value: number;
  color?: string;
  id?: number | null;
} & TData;

export type PieChartProps<TData extends object = object> = {
  className?: string;
  data: PieData<TData>[];
  donut?: boolean;
  donutWidth?: number;
  showLegend?: boolean;
  colors?: string[];
  tooltipContent?: (data: PieData<TData>) => React.ReactNode;
  highlightOnHover?: boolean;
} & CommonPlotProps;

export const PieChart = <TData extends object = object>({
  className,
  data = [],
  donut = false,
  donutWidth = 0.6,
  showLegend = true,
  format = FormatType.INTEGER,
  currencyCode = 'USD',
  loading = false,
  colors: colorsProp,
  tooltipContent,
  highlightOnHover = true,
}: PieChartProps<TData>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { palette, theme } = useChartTheme();
  const colors = colorsProp ?? palette;

  const { width, height } = useResizeObserver(containerRef);

  const chartHeight = showLegend ? height - LEGEND_HEIGHT : height;
  const padding = width < 100 ? 5 : 20;
  const size = Math.min(width, chartHeight) - padding * 2;
  const radius = size / 2;
  const innerRadius = donut ? radius * donutWidth : 0;

  const isPlotSizeValid = size >= MIN_SIZE;

  const allValues = data.map((d) => d.value);
  const total = allValues.reduce((sum, val) => sum + val, 0);

  const tooltipFormatValue = getFormatter(
    format === FormatType.SI ? FormatType.INTEGER : format,
    { values: allValues, increasePrecision: 1, currencyCode }
  );

  const dataWithColors = data.map((d, i) => ({
    ...d,
    color: d.color || colors[i % colors.length] || getPrimaryColor(),
  }));

  const pieGenerator = pie<PieData<TData>>()
    .value((d) => d.value)
    .sort(null);

  const arcs = pieGenerator(dataWithColors);

  const arcGenerator = arc<PieArcDatum<PieData<TData>>>()
    .innerRadius(innerRadius)
    .outerRadius(radius);

  const arcGeneratorHover = arc<PieArcDatum<PieData<TData>>>()
    .innerRadius(innerRadius)
    .outerRadius(radius * 1.05);

  const legendData = dataWithColors.map((d) => ({
    label: d.label,
    color: d.color || getPrimaryColor(),
  }));

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-testid={plotTestId(ReplotChartNames.PieChart)}
    >
      <PlotLoadWrapper loading={loading} noData={data.length === 0}>
        <svg width="100%" height={showLegend ? chartHeight : '100%'}>
          <g
            transform={`translate(${width / 2}, ${chartHeight / 2})`}
            className={classNames({
              [styles.highlightOnHover ?? '']: highlightOnHover,
            })}
          >
            {isPlotSizeValid &&
              arcs.map((d, i) => {
                const isHovered = hoveredIndex === i;
                const pathData = isHovered
                  ? arcGeneratorHover(d)
                  : arcGenerator(d);
                const percentage = total > 0 ? (d.data.value / total) * 100 : 0;

                return (
                  <g key={`slice-${d.data.label}-${i}`}>
                    <CursorTooltip
                      label={
                        tooltipContent ? (
                          tooltipContent(d.data)
                        ) : (
                          <div>
                            <div className={styles.pieTooltipLabel}>
                              {d.data.label}
                            </div>
                            <div className={styles.pieTooltipValue}>
                              {tooltipFormatValue(d.data.value)} (
                              {percentage.toFixed(1)}%)
                            </div>
                          </div>
                        )
                      }
                    >
                      <path
                        d={pathData || ''}
                        fill={d.data.color}
                        style={
                          {
                            '--replots-dim-color': getDimmedPlotColor(
                              d.data.color ?? getPrimaryColor(),
                              theme
                            ),
                          } as CSSProperties
                        }
                        className={styles.slice}
                        data-testid="pie-chart__slice"
                        onMouseEnter={() => setHoveredIndex(i)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      />
                    </CursorTooltip>
                  </g>
                );
              })}
          </g>
        </svg>
        {showLegend && (
          <div className={styles.legendContainer}>
            <Legend items={legendData} />
          </div>
        )}
      </PlotLoadWrapper>
    </div>
  );
};
